import { Maybe, Optional } from '../types';

// Helper function to remove undefined | null | '' fields from an object
export function cleanEmptyFields<T extends Record<string, any>>(
  obj: T,
): Partial<T> {
  // Type guard for empty values
  const isEmptyValue = (value: any): boolean =>
    value === undefined || value === null || value === '';

  // Type guard for empty objects
  const isEmptyObject = (value: any): boolean =>
    typeof value === 'object' &&
    value !== null &&
    Object.keys(value).length === 0;

  function sanitizeRecursive(input: any): any {
    if (typeof input !== 'object' || input === null || input === undefined) {
      return input;
    }

    const sanitizedObj: any = Array.isArray(input) ? [] : {};
    for (const key in input) {
      if (Object.prototype.hasOwnProperty.call(input, key)) {
        const value = input[key];

        if (typeof value === 'object' && value !== null) {
          const nested = sanitizeRecursive(value);
          if (!isEmptyObject(nested)) {
            sanitizedObj[key] = nested;
          }
        } else if (!isEmptyValue(value)) {
          sanitizedObj[key] = value;
        }
      }
    }
    return sanitizedObj;
  }

  return typeof obj === 'object' && obj !== null ? sanitizeRecursive(obj) : {};
}

export const schemaIsRequired = (schema: any) => {
  return Boolean(
    schema
      ?.describe?.()
      ?.tests?.some?.((test: { name?: string }) => test.name === 'required'),
  );
};

export function hasInvalidValues(
  localisationSettings: Record<string, unknown>,
  template: Record<string, any>,
): boolean {
  if (!localisationSettings) {
    return true;
  }

  // Iterate over each entry in localisationSettings
  return Object.entries(localisationSettings).some(([key, value]) => {
    // Check if the current field is required based on its template schema
    const isRequired = schemaIsRequired(template[key]?.validationSchema);

    // If the field is required, apply the validation logic
    if (isRequired) {
      const isInvalid =
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0);

      // If a required field is invalid, return true
      if (isInvalid) {
        return true;
      }
    }
    // If the field is not required, or if it's required and valid, continue to the next field
    return false;
  });
}

/**
 * Checks if a value is an object (including functions and arrays) but not null.
 */
export function isObject(value: any): boolean {
  return (
    value !== null && (typeof value === 'object' || typeof value === 'function')
  );
}

/**
 * Checks if the provided path exists in the object.
 * The path can be a dot-delimited string (e.g. "a.b.c") or an array of keys.
 */
export function has(
  object: any,
  path: string | Array<string | number>,
): boolean {
  if (object == null) return false;

  let keys: Array<string | number>;
  if (typeof path === 'string') {
    // Split the string on dots, filtering out any empty strings.
    keys = path.split('.').filter(Boolean);
  } else {
    keys = path;
  }

  let current = object;
  for (const key of keys) {
    if (current == null || !(key in current)) {
      return false;
    }
    current = current[key];
  }
  return true;
}

/**
 * Recursively merges own enumerable properties of source objects into the target object.
 * An optional customizer function may be provided to determine how values are merged.
 *
 * If the customizer returns `undefined` for a given key, merging is handled by default:
 *   - If both target and source values are objects (or arrays), they are merged recursively.
 *   - Otherwise, the source value replaces the target value.
 *
 * The function supports multiple source objects. If the last argument is a function,
 * it is assumed to be the customizer.
 */
export function mergeWith(target: any, ...args: any[]): any {
  let customizer:
    | ((
        targetVal: any,
        sourceVal: any,
        key: string,
        target: any,
        source: any,
      ) => any)
    | undefined;
  // If the last argument is a function, treat it as the customizer.
  if (args.length && typeof args[args.length - 1] === 'function') {
    customizer = args.pop();
  }
  const sources = args;

  sources.forEach(source => {
    if (source == null) return;
    Object.keys(source).forEach(key => {
      const targetValue = target[key];
      const sourceValue = source[key];

      // If a customizer is provided, call it.
      const newValue = customizer
        ? customizer(targetValue, sourceValue, key, target, source)
        : undefined;

      if (newValue === undefined) {
        // If both target and source values are objects, merge them recursively.
        if (isObject(targetValue) && isObject(sourceValue)) {
          target[key] = mergeWith(
            // Create a shallow copy of the targetValue to avoid mutation.
            Array.isArray(targetValue) ? [...targetValue] : { ...targetValue },
            sourceValue,
            customizer,
          );
        } else {
          target[key] = sourceValue;
        }
      } else {
        target[key] = newValue;
      }
    });
  });

  return target;
}

/**
 * Checks if a given object is non-null, non-undefined, and contains at least one key-value pair.
 * Only applies to plain objects (not arrays or other types).
 *
 * @param obj - The value to check, which can be an optional object, null, or undefined.
 * @returns `true` if the value is a non-empty plain object, `false` otherwise.
 * @example
 * ```typescript
 * hasObjectContent({ a: 1 }); // Returns true
 * hasObjectContent({}); // Returns false
 * hasObjectContent(null); // Returns false
 * hasObjectContent(undefined); // Returns false
 * hasObjectContent([1, 2]); // Returns false
 * ```
 */
export const hasObjectContent = (obj: Optional<Maybe<Record<any, any>>>) => {
  if (obj === null || obj === undefined) {
    return false;
  }
  if (typeof obj === 'object' && !Array.isArray(obj)) {
    return Object.keys(obj).length > 0;
  }
  return false;
};

/**
 * Performs a deep comparison between two values to determine if they are equal.
 * Recursively compares objects and arrays, checking both structure and values.
 * Handles primitive types, arrays, and nested objects, including edge cases like null.
 *
 * @param obj1 - The first value to compare (can be any type).
 * @param obj2 - The second value to compare (can be any type).
 * @returns `true` if the values are deeply equal, `false` otherwise.
 * @example
 * ```typescript
 * deepCompare({ a: 1, b: { c: 2 } }, { a: 1, b: { c: 2 } }); // Returns true
 * deepCompare([1, 2, 3], [1, 2, 3]); // Returns true
 * deepCompare({ a: 1 }, { a: 2 }); // Returns false
 * deepCompare(null, undefined); // Returns false
 * ```
 */
export const deepCompare = (obj1: any, obj2: any) => {
  if (obj1 === obj2) {
    return true;
  }

  if (obj1 == null || obj2 == null) {
    return obj1 === obj2;
  }

  // Check if one is an array and the other is not
  if (Array.isArray(obj1) !== Array.isArray(obj2)) {
    return false;
  }

  if (typeof obj1 !== typeof obj2) {
    return false;
  }

  if (Array.isArray(obj1)) {
    if (obj1.length !== obj2.length) {
      return false;
    }

    for (let i = 0; i < obj1.length; i++) {
      if (!deepCompare(obj1[i], obj2[i])) {
        return false;
      }
    }
    return true;
  }

  if (typeof obj1 === 'object') {
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      if (!keys2.includes(key) || !deepCompare(obj1[key], obj2[key])) {
        return false;
      }
    }

    return true;
  }

  return obj1 === obj2;
};
